import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, FlaskConical, Droplet, Beaker, Plus,
  RefreshCw, Trash2, Send, Mail,
  Paperclip, X, FileText, Camera, Calendar, Search, History, UserCircle, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BATCH_STATUSES = ['pending', 'pass', 'reject', 'rework', 'hold'];
const RM_STATUSES = ['pending', 'accept', 'reject', 'rework'];
const SAMPLE_STATUSES = ['pending', 'accept', 'reject', 'rework', 'approved'];

const STATUS_COLOR = {
  pending: 'bg-zinc-800 border-zinc-700 text-zinc-300',
  pass: 'bg-green-900/40 border-green-700 text-green-300',
  accept: 'bg-green-900/40 border-green-700 text-green-300',
  approved: 'bg-green-900/40 border-green-700 text-green-300',
  reject: 'bg-red-900/40 border-red-700 text-red-300',
  rework: 'bg-amber-900/40 border-amber-700 text-amber-300',
  hold: 'bg-amber-900/40 border-amber-700 text-amber-300',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function QCTasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const [tab, setTab] = useState('batches');
  const [viewDate, setViewDate] = useState(todayStr());
  const [filterUser, setFilterUser] = useState('');  // empty = all users (admin only)
  const [users, setUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [dash, setDash] = useState(null);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const isToday = viewDate === todayStr();
  const canEdit = isAdmin || isToday;

  const loadDash = async () => {
    try {
      const [dashRes, usersRes, activeRes] = await Promise.all([
        axios.get(`${API}/qc/dashboard`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/qc/users-active`, { withCredentials: true }).catch(() => ({ data: { users: [] } })),
      ]);
      setDash(dashRes.data);
      setUsers((usersRes.data || []).filter(u => ['qc', 'admin', 'owner', 'manager'].includes(u.role)));
      setActiveUsers(activeRes.data?.users || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadDash(); }, []);

  const sendDailyEmail = async () => {
    if (!window.confirm(`Send the 3-table QC digest for ${viewDate}${filterUser ? ' (user: ' + filterUser + ')' : ''} to the designated recipients?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/qc/send-daily-email`, {
        date: viewDate,
        user_name: filterUser || undefined,
      }, { withCredentials: true });
      if (res.data?.email_status?.sent) {
        toast.success(`Digest emailed to ${res.data.recipients.length} recipient(s)`);
      } else {
        toast.info(res.data?.email_status?.reason || 'Digest saved (email not configured)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div data-testid="qc-page" className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ClipboardCheck className="text-sky-400" size={24} /> Quality Control
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Batch inspections · Raw-material COA · Samples. Only today's rows are editable (admin can edit any date).</p>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setShowHistory(v => !v)} data-testid="qc-history-toggle"
            className={`p-2 rounded-lg border text-sm flex items-center gap-1.5 ${showHistory ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border text-foreground hover:bg-accent'}`}
          ><History size={14} /> {showHistory ? 'Close history' : 'History / Search'}</button>
          <button onClick={loadDash} data-testid="qc-refresh" className="p-2 rounded-lg bg-secondary hover:bg-accent text-foreground border border-border"><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Today — Batch QC" value={dash?.batch_today ?? '—'} testid="qc-kpi-batch-today" />
        <KPI label="Pending Batches" value={dash?.batch_pending ?? '—'} color="text-amber-400" testid="qc-kpi-batch-pending" />
        <KPI label="Rejected (30d)" value={dash?.batch_rejected_30d ?? '—'} color="text-red-400" testid="qc-kpi-reject" />
        <KPI label="Today — RM QC" value={dash?.rm_today ?? '—'} testid="qc-kpi-rm-today" />
        <KPI label="Pending Samples" value={dash?.samples_pending ?? '—'} color="text-amber-400" testid="qc-kpi-samples-pending" />
      </div>

      {/* User scroller · shown to everyone (filter by feeder) */}
      {activeUsers.length > 0 && !showHistory && (
        <div className="bg-card border border-border rounded-xl p-3">
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <UserCircle size={12} /> QC users feeding entries · tap to filter
          </div>          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              data-testid="qc-user-filter-all"
              onClick={() => setFilterUser('')}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${filterUser === '' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground hover:bg-accent'}`}
            >All users</button>
            {activeUsers.map(u => (
              <button
                key={u.user_name}
                data-testid={`qc-user-filter-${u.user_name}`}
                onClick={() => setFilterUser(u.user_name)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1.5 ${filterUser === u.user_name ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-foreground hover:bg-accent'}`}
              >
                <UserCircle size={11} />
                {u.user_name}
                <span className="text-[10px] opacity-70">· {u.total}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showHistory ? (
        <QCHistory users={activeUsers} />
      ) : (
        <>
          {/* Date + Send-email bar */}
          <div className="bg-card border border-border rounded-xl p-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar size={13} /> Viewing date:
                <input
                  type="date"
                  value={viewDate}
                  data-testid="qc-view-date"
                  onChange={e => setViewDate(e.target.value)}
                  className="bg-background border border-border rounded px-2 py-1 text-xs text-foreground"
                />
              </label>
              {!isToday && (
                <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                  <Lock size={10} /> read-only{isAdmin ? ' (admin can edit)' : ''}
                </span>
              )}
              {filterUser && (
                <span className="text-[11px] bg-sky-500/10 text-sky-300 border border-sky-500/30 rounded-full px-2 py-0.5">
                  User: {filterUser}
                </span>
              )}
            </div>
            {isAdmin && (
              <button
                onClick={sendDailyEmail} disabled={sending}
                data-testid="qc-send-email"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:opacity-90 font-semibold disabled:opacity-50"
              >
                {sending ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={12} />}
                Email 3-table digest
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            <TabBtn active={tab === 'batches'} onClick={() => setTab('batches')} testid="qc-tab-batches"><ClipboardCheck size={14} /> Batch QC</TabBtn>
            <TabBtn active={tab === 'raw'} onClick={() => setTab('raw')} testid="qc-tab-raw"><Droplet size={14} /> Raw Material + Lot (COA)</TabBtn>
            <TabBtn active={tab === 'samples'} onClick={() => setTab('samples')} testid="qc-tab-samples"><Beaker size={14} /> Samples</TabBtn>
          </div>

          {tab === 'batches' && <BatchQCSheet users={users} onChange={loadDash} viewDate={viewDate} filterUser={filterUser} canEdit={canEdit} isAdmin={isAdmin} />}
          {tab === 'raw' && <RawMaterialQCSheet users={users} onChange={loadDash} viewDate={viewDate} filterUser={filterUser} canEdit={canEdit} isAdmin={isAdmin} />}
          {tab === 'samples' && <SamplesSheet users={users} onChange={loadDash} viewDate={viewDate} filterUser={filterUser} canEdit={canEdit} isAdmin={isAdmin} />}
        </>
      )}
    </div>
  );
}

const KPI = ({ label, value, color, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-xl p-3">
    <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
    <div className={`text-xl font-bold mt-1 ${color || 'text-foreground'}`}>{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${
      active ? 'bg-sky-600 border-sky-500 text-white' : 'bg-secondary border-border text-foreground hover:bg-accent'
    }`}
  >{children}</button>
);

// ─── History / Search tab ────────────────────────────────────────────────
function QCHistory() {
  const [filters, setFilters] = useState({ from_date: '', to_date: '', user_name: '', batch_ref: '', lot_no: '', material: '' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/qc/history`, { params, withCredentials: true });
      setRows(res.data?.items || []);
    } catch (err) {
      toast.error('Search failed');
    } finally { setLoading(false); }
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3">
      <div className="bg-card border border-border rounded-xl p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
        <input type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} placeholder="From" className="bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-from" />
        <input type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} placeholder="To" className="bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-to" />
        <input value={filters.user_name} onChange={e => setFilters({ ...filters, user_name: e.target.value })} placeholder="User name" className="bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-user" />
        <input value={filters.batch_ref} onChange={e => setFilters({ ...filters, batch_ref: e.target.value })} placeholder="Batch ref" className="bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-batch" />
        <input value={filters.lot_no} onChange={e => setFilters({ ...filters, lot_no: e.target.value })} placeholder="Lot #" className="bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-lot" />
        <div className="flex gap-2">
          <input value={filters.material} onChange={e => setFilters({ ...filters, material: e.target.value })} placeholder="Material" className="flex-1 bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground" data-testid="qc-hist-material" />
          <button onClick={search} data-testid="qc-hist-search" className="px-3 py-1 text-xs rounded-lg bg-primary text-primary-foreground font-semibold flex items-center gap-1"><Search size={12} /> Go</button>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/60 text-muted-foreground uppercase text-[10px]">
              <tr>
                <th className="px-2 py-2 text-left">Kind</th>
                <th className="px-2 py-2 text-left">Date</th>
                <th className="px-2 py-2 text-left">User</th>
                <th className="px-2 py-2 text-left">Ref / Lot / Material</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                : rows.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No history rows match your filters.</td></tr>
                  : rows.map(r => (
                    <tr key={r.id} className="border-t border-border hover:bg-accent/40">
                      <td className="px-2 py-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${r._kind === 'batch' ? 'bg-sky-500/15 text-sky-300' : r._kind === 'raw_material' ? 'bg-blue-500/15 text-blue-300' : 'bg-violet-500/15 text-violet-300'}`}>
                          {r._kind === 'batch' ? 'Batch' : r._kind === 'raw_material' ? 'RM' : 'Sample'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-foreground font-mono">{r.check_date}</td>
                      <td className="px-2 py-1.5 text-foreground">{r.qc_user_name || '—'}</td>
                      <td className="px-2 py-1.5 text-foreground">
                        {r._kind === 'batch' && <span>{r.batch_ref} · {r.product_name}</span>}
                        {r._kind === 'raw_material' && <span>{r.material_name} · lot {r.lot_no || '—'}</span>}
                        {r._kind === 'sample' && <span>{r.sample_ref} · {r.source_name || r.product_name || '—'}</span>}
                      </td>
                      <td className="px-2 py-1.5"><span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-medium border ${STATUS_COLOR[r.status] || STATUS_COLOR.pending}`}>{r.status}</span></td>
                      <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[320px]" title={r.remarks}>{r.remarks || '—'}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Attachment cell (unchanged functionality, refactored styles) ────────
function AttachmentCell({ kind, rowId, attachments, onChanged, disabled }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const list = attachments || [];

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (list.length >= 5) { toast.error('Max 5 attachments per row'); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error('Max 3 MB per file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await axios.post(`${API}/qc/${kind}/${rowId}/attachments`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Attached');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove ${att.filename}?`)) return;
    try {
      await axios.delete(`${API}/qc/attachments/${att.id}`, { withCredentials: true });
      toast.success('Removed');
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="flex items-center gap-1">
      {list.slice(0, 3).map(a => {
        const isImage = (a.content_type || '').startsWith('image/');
        return (
          <div key={a.id} className="relative group shrink-0">
            <button data-testid={`qc-att-open-${a.id}`} onClick={() => setPreview(a)} title={a.filename}
              className="w-7 h-7 rounded border border-border bg-background hover:border-sky-500 overflow-hidden flex items-center justify-center">
              {isImage
                ? <img src={`${API}/qc/attachments/${a.id}`} alt="" className="w-full h-full object-cover" />
                : <FileText size={12} className="text-red-400" />}
            </button>
            {!disabled && (
              <button data-testid={`qc-att-del-${a.id}`} onClick={() => remove(a)}
                className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove">
                <X size={8} className="text-white" />
              </button>
            )}
          </div>
        );
      })}
      {list.length > 3 && <span className="text-[10px] text-muted-foreground">+{list.length - 3}</span>}
      {list.length < 5 && !disabled && (
        <>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid={`qc-att-input-${rowId}`} />
          <button data-testid={`qc-att-add-${rowId}`} onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-7 h-7 rounded border border-dashed border-border hover:border-sky-500 text-muted-foreground hover:text-sky-400 flex items-center justify-center shrink-0"
            title="Attach photo or PDF (COA)">
            {uploading ? <RefreshCw size={10} className="animate-spin" /> : <Camera size={12} />}
          </button>
        </>
      )}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="qc-att-preview">
          <div className="bg-card border border-border rounded-xl max-w-4xl max-h-full overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 gap-3">
              <div>
                <div className="text-sm font-bold text-foreground">{preview.filename}</div>
                <div className="text-xs text-muted-foreground">{preview.content_type} · {(preview.size / 1024).toFixed(1)} KB · uploaded by {preview.uploaded_by_name || preview.uploaded_by}</div>
              </div>
              <div className="flex gap-2">
                <a href={`${API}/qc/attachments/${preview.id}`} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold">Open original</a>
                <button onClick={() => setPreview(null)} className="px-3 py-1.5 rounded-lg bg-secondary text-foreground text-xs"><X size={14} /></button>
              </div>
            </div>
            {(preview.content_type || '').startsWith('image/')
              ? <img src={`${API}/qc/attachments/${preview.id}`} alt={preview.filename} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
              : <iframe title={preview.filename} src={`${API}/qc/attachments/${preview.id}`} className="w-[80vw] h-[70vh] rounded-lg bg-white" />}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Editable inputs ─────────────────────────────────────────────────────
const EditableInput = ({ value, onChange, placeholder, type = 'text', width = 'min-w-[100px] w-full', testid, disabled }) => (
  <input
    data-testid={testid}
    type={type}
    disabled={disabled}
    value={value === null || value === undefined ? '' : value}
    onChange={e => onChange(type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
    placeholder={placeholder}
    className={`${width} bg-background border border-border rounded px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus:border-sky-500 disabled:opacity-60 disabled:cursor-not-allowed`}
  />
);

const EditableSelect = ({ value, options, onChange, testid, disabled }) => (
  <select
    data-testid={testid}
    value={value || ''}
    disabled={disabled}
    onChange={e => onChange(e.target.value)}
    className="min-w-[110px] w-full bg-background border border-border rounded px-2 py-1.5 text-[11px] text-foreground focus:outline-none focus:border-sky-500 disabled:opacity-60"
  >
    {options.map(o => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

// ═════════════ Batch QC Sheet ═════════════════════════════════════════════
function BatchQCSheet({ users, onChange, viewDate, filterUser, canEdit, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(emptyBatchRow());

  function emptyBatchRow() {
    return {
      batch_ref: '', product_name: '', line: 'PL1', qc_user_name: '',
      status: 'pending', check_date: viewDate, check_time: '',
      color: '', aroma: '', texture: '', taste: '', moisture_pct: null,
      foreign_matter: '', remarks: '',
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qc/daily`, { params: { date: viewDate, user_name: filterUser || undefined }, withCredentials: true });
      setRows(res.data?.batches || []);
    } catch { toast.error('Failed to load batch QC'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setNewRow(emptyBatchRow()); load(); /* eslint-disable-next-line */ }, [viewDate, filterUser]);

  const addRow = async () => {
    if (!newRow.batch_ref || !newRow.product_name) { toast.error('Batch ref and product required'); return; }
    try {
      await axios.post(`${API}/qc/batches`, { ...newRow, check_date: viewDate }, { withCredentials: true });
      toast.success('QC row added');
      setNewRow(emptyBatchRow()); load(); onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
  };

  const updateRow = async (row, patch) => {
    try {
      await axios.patch(`${API}/qc/batches/${row.id}`, patch, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete this QC row?')) return;
    try { await axios.delete(`${API}/qc/batches/${row.id}`, { withCredentials: true }); load(); onChange?.(); }
    catch (err) { toast.error('Delete denied (admin only)'); }
  };

  const qcUserOpts = useMemo(() => [{ value: '', label: '— Select QC —' }, ...users.filter(u => u.role === 'qc' || u.role === 'admin' || u.role === 'owner' || u.role === 'manager').map(u => ({ value: u.name, label: u.name }))], [users]);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="qc-batch-sheet">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 1400 }}>
          <thead className="bg-secondary/60 text-muted-foreground uppercase text-[10px]">
            <tr>
              {['Batch Ref', 'Product', 'Line', 'QC User', 'Status', 'Time', 'Color', 'Aroma', 'Texture', 'Moist%', 'FM', 'Remarks', 'COA / Photos', ''].map(h => (
                <th key={h} className="px-3 py-2 text-left whitespace-nowrap border-r border-border last:border-r-0">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {canEdit && (
              <tr className="bg-sky-900/20 border-t border-sky-700/40" data-testid="qc-batch-new-row">
                <td className="px-2 py-1.5"><EditableInput testid="new-batch-ref" value={newRow.batch_ref} onChange={v => setNewRow({ ...newRow, batch_ref: v })} placeholder="260422A" /></td>
                <td className="px-2 py-1.5"><EditableInput testid="new-batch-product" value={newRow.product_name} onChange={v => setNewRow({ ...newRow, product_name: v })} placeholder="Tomato Masala" /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.line} options={['PL1', 'PL2', 'PL3']} onChange={v => setNewRow({ ...newRow, line: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.qc_user_name} options={qcUserOpts} onChange={v => setNewRow({ ...newRow, qc_user_name: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.status} options={BATCH_STATUSES} onChange={v => setNewRow({ ...newRow, status: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.check_time} onChange={v => setNewRow({ ...newRow, check_time: v })} placeholder="HH:MM" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.color} onChange={v => setNewRow({ ...newRow, color: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.aroma} onChange={v => setNewRow({ ...newRow, aroma: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.texture} onChange={v => setNewRow({ ...newRow, texture: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.moisture_pct} onChange={v => setNewRow({ ...newRow, moisture_pct: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.foreign_matter} onChange={v => setNewRow({ ...newRow, foreign_matter: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.remarks} onChange={v => setNewRow({ ...newRow, remarks: v })} /></td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">after add</td>
                <td className="px-2 py-1.5"><button data-testid="qc-batch-add-btn" onClick={addRow} className="p-1.5 rounded-md bg-sky-600 hover:bg-sky-500 text-white"><Plus size={12} /></button></td>
              </tr>
            )}
            {loading ? <tr><td colSpan={14} className="p-6 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={14} className="p-6 text-center text-muted-foreground">{canEdit ? 'No batch QC rows yet. Add the first row above.' : 'No batch QC entries on this date.'}</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} data-testid={`qc-batch-row-${r.id}`} className="border-t border-border hover:bg-accent/40">
                    <td className="px-2 py-1.5 font-mono text-foreground">{r.batch_ref}</td>
                    <td className="px-2 py-1.5 text-foreground">{r.product_name}</td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.line} onChange={v => updateRow(r, { line: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.qc_user_name || ''} options={qcUserOpts} onChange={v => updateRow(r, { qc_user_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.status} options={BATCH_STATUSES} onChange={v => updateRow(r, { status: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.check_time || ''} onChange={v => updateRow(r, { check_time: v })} placeholder="HH:MM" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.color || ''} onChange={v => updateRow(r, { color: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.aroma || ''} onChange={v => updateRow(r, { aroma: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.texture || ''} onChange={v => updateRow(r, { texture: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.moisture_pct} onChange={v => updateRow(r, { moisture_pct: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.foreign_matter || ''} onChange={v => updateRow(r, { foreign_matter: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.remarks || ''} onChange={v => updateRow(r, { remarks: v })} /></td>
                    <td className="px-2 py-1.5"><AttachmentCell kind="batches" rowId={r.id} attachments={r.attachments} onChanged={load} disabled={!canEdit} /></td>
                    <td className="px-2 py-1.5">{isAdmin && <button data-testid={`qc-batch-del-${r.id}`} onClick={() => deleteRow(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-900/30"><Trash2 size={11} /></button>}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═════════════ Raw Material QC Sheet ═════════════════════════════════════
function RawMaterialQCSheet({ users, onChange, viewDate, filterUser, canEdit, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(emptyRow());

  function emptyRow() {
    return {
      material_name: '', lot_no: '', vendor_name: '', qc_user_name: '',
      status: 'pending', check_date: viewDate, check_time: '',
      moisture_pct: null, ash_pct: null, color: '', ph: null,
      microbial: '', oil_pct: null, mesh_size: '', remarks: '',
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qc/daily`, { params: { date: viewDate, user_name: filterUser || undefined }, withCredentials: true });
      setRows(res.data?.raw_materials || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { setNewRow(emptyRow()); load(); /* eslint-disable-next-line */ }, [viewDate, filterUser]);

  const addRow = async () => {
    if (!newRow.material_name) { toast.error('Material name required'); return; }
    try {
      await axios.post(`${API}/qc/raw-materials`, { ...newRow, check_date: viewDate }, { withCredentials: true });
      toast.success('Added'); setNewRow(emptyRow()); load(); onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
  };

  const updateRow = async (row, patch) => {
    try {
      await axios.patch(`${API}/qc/raw-materials/${row.id}`, patch, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete row?')) return;
    try { await axios.delete(`${API}/qc/raw-materials/${row.id}`, { withCredentials: true }); load(); onChange?.(); }
    catch { toast.error('Delete denied'); }
  };

  const qcUserOpts = [{ value: '', label: '— Select QC —' }, ...users.map(u => ({ value: u.name, label: u.name }))];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="qc-rm-sheet">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 1600 }}>
          <thead className="bg-secondary/60 text-muted-foreground uppercase text-[10px]">
            <tr>
              {['Material', 'Lot#', 'Vendor', 'QC User', 'Status', 'Time', 'Moist%', 'Ash%', 'Color', 'pH', 'Oil%', 'Mesh', 'Microbial', 'Remarks', 'COA', ''].map(h => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {canEdit && (
              <tr className="bg-sky-900/20 border-t border-sky-700/40" data-testid="qc-rm-new-row">
                <td className="px-2 py-1.5"><EditableInput testid="new-rm-name" value={newRow.material_name} onChange={v => setNewRow({ ...newRow, material_name: v })} placeholder="Turmeric" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.lot_no} onChange={v => setNewRow({ ...newRow, lot_no: v })} placeholder="LOT-001" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.vendor_name} onChange={v => setNewRow({ ...newRow, vendor_name: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.qc_user_name} options={qcUserOpts} onChange={v => setNewRow({ ...newRow, qc_user_name: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.status} options={RM_STATUSES} onChange={v => setNewRow({ ...newRow, status: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.check_time} onChange={v => setNewRow({ ...newRow, check_time: v })} placeholder="HH:MM" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.moisture_pct} onChange={v => setNewRow({ ...newRow, moisture_pct: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.ash_pct} onChange={v => setNewRow({ ...newRow, ash_pct: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.color} onChange={v => setNewRow({ ...newRow, color: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.ph} onChange={v => setNewRow({ ...newRow, ph: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.oil_pct} onChange={v => setNewRow({ ...newRow, oil_pct: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.mesh_size} onChange={v => setNewRow({ ...newRow, mesh_size: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.microbial} onChange={v => setNewRow({ ...newRow, microbial: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.remarks} onChange={v => setNewRow({ ...newRow, remarks: v })} /></td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">after add</td>
                <td className="px-2 py-1.5"><button data-testid="qc-rm-add-btn" onClick={addRow} className="p-1.5 rounded-md bg-sky-600 hover:bg-sky-500 text-white"><Plus size={12} /></button></td>
              </tr>
            )}
            {loading ? <tr><td colSpan={16} className="p-6 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={16} className="p-6 text-center text-muted-foreground">{canEdit ? 'No raw-material QC rows yet.' : 'No entries on this date.'}</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} data-testid={`qc-rm-row-${r.id}`} className="border-t border-border hover:bg-accent/40">
                    <td className="px-2 py-1.5 text-foreground font-medium">{r.material_name}</td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.lot_no || ''} onChange={v => updateRow(r, { lot_no: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.vendor_name || ''} onChange={v => updateRow(r, { vendor_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.qc_user_name || ''} options={qcUserOpts} onChange={v => updateRow(r, { qc_user_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.status} options={RM_STATUSES} onChange={v => updateRow(r, { status: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.check_time || ''} onChange={v => updateRow(r, { check_time: v })} placeholder="HH:MM" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.moisture_pct} onChange={v => updateRow(r, { moisture_pct: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.ash_pct} onChange={v => updateRow(r, { ash_pct: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.color || ''} onChange={v => updateRow(r, { color: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.ph} onChange={v => updateRow(r, { ph: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.oil_pct} onChange={v => updateRow(r, { oil_pct: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.mesh_size || ''} onChange={v => updateRow(r, { mesh_size: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.microbial || ''} onChange={v => updateRow(r, { microbial: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.remarks || ''} onChange={v => updateRow(r, { remarks: v })} /></td>
                    <td className="px-2 py-1.5"><AttachmentCell kind="raw-materials" rowId={r.id} attachments={r.attachments} onChanged={load} disabled={!canEdit} /></td>
                    <td className="px-2 py-1.5">{isAdmin && <button data-testid={`qc-rm-del-${r.id}`} onClick={() => deleteRow(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-900/30"><Trash2 size={11} /></button>}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═════════════ Samples Sheet ═════════════════════════════════════════════
function SamplesSheet({ users, onChange, viewDate, filterUser, canEdit, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(emptyRow());

  function emptyRow() {
    return {
      sample_type: 'raw_material_sample', sample_ref: '', source_name: '',
      product_name: '', batch_ref: '', client_name: '',
      qc_user_name: '', status: 'pending',
      check_date: viewDate, check_time: '',
      taste: '', aroma: '', color: '', texture: '', moisture_pct: null,
      approval: '', remarks: '',
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qc/daily`, { params: { date: viewDate, user_name: filterUser || undefined }, withCredentials: true });
      setRows(res.data?.samples || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { setNewRow(emptyRow()); load(); /* eslint-disable-next-line */ }, [viewDate, filterUser]);

  const addRow = async () => {
    if (!newRow.sample_ref) { toast.error('Sample ref required'); return; }
    try {
      await axios.post(`${API}/qc/samples`, { ...newRow, check_date: viewDate }, { withCredentials: true });
      toast.success('Sample added'); setNewRow(emptyRow()); load(); onChange?.();
    } catch { toast.error('Add failed'); }
  };

  const updateRow = async (row, patch) => {
    try {
      await axios.patch(`${API}/qc/samples/${row.id}`, patch, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete sample?')) return;
    try { await axios.delete(`${API}/qc/samples/${row.id}`, { withCredentials: true }); load(); onChange?.(); }
    catch { toast.error('Delete denied'); }
  };

  const qcUserOpts = [{ value: '', label: '— Select QC —' }, ...users.map(u => ({ value: u.name, label: u.name }))];
  const typeOpts = [
    { value: 'raw_material_sample', label: 'RM Sample (COA)' },
    { value: 'npd_sample', label: 'NPD Sample' },
    { value: 'production_sample', label: 'Production Sample' },
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="qc-sample-sheet">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" style={{ minWidth: 1500 }}>
          <thead className="bg-secondary/60 text-muted-foreground uppercase text-[10px]">
            <tr>
              {['Type', 'Ref', 'Source', 'Client (opt)', 'QC User', 'Status', 'Time', 'Taste', 'Aroma', 'Color', 'Moist%', 'Approval', 'Remarks', 'COA', ''].map(h => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {canEdit && (
              <tr className="bg-sky-900/20 border-t border-sky-700/40" data-testid="qc-sample-new-row">
                <td className="px-2 py-1.5"><EditableSelect value={newRow.sample_type} options={typeOpts} onChange={v => setNewRow({ ...newRow, sample_type: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput testid="new-sample-ref" value={newRow.sample_ref} onChange={v => setNewRow({ ...newRow, sample_ref: v })} placeholder="NPD-042" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.source_name} onChange={v => setNewRow({ ...newRow, source_name: v })} placeholder="Material/Product" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.client_name} onChange={v => setNewRow({ ...newRow, client_name: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.qc_user_name} options={qcUserOpts} onChange={v => setNewRow({ ...newRow, qc_user_name: v })} /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.status} options={SAMPLE_STATUSES} onChange={v => setNewRow({ ...newRow, status: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.check_time} onChange={v => setNewRow({ ...newRow, check_time: v })} placeholder="HH:MM" /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.taste} onChange={v => setNewRow({ ...newRow, taste: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.aroma} onChange={v => setNewRow({ ...newRow, aroma: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.color} onChange={v => setNewRow({ ...newRow, color: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.moisture_pct} onChange={v => setNewRow({ ...newRow, moisture_pct: v })} type="number" /></td>
                <td className="px-2 py-1.5"><EditableSelect value={newRow.approval} options={['', 'Yes', 'No', 'Hold']} onChange={v => setNewRow({ ...newRow, approval: v })} /></td>
                <td className="px-2 py-1.5"><EditableInput value={newRow.remarks} onChange={v => setNewRow({ ...newRow, remarks: v })} /></td>
                <td className="px-2 py-1.5 text-[10px] text-muted-foreground">after add</td>
                <td className="px-2 py-1.5"><button data-testid="qc-sample-add-btn" onClick={addRow} className="p-1.5 rounded-md bg-sky-600 hover:bg-sky-500 text-white"><Plus size={12} /></button></td>
              </tr>
            )}
            {loading ? <tr><td colSpan={15} className="p-6 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={15} className="p-6 text-center text-muted-foreground">{canEdit ? 'No samples yet.' : 'No entries on this date.'}</td></tr>
                : rows.map((r) => (
                  <tr key={r.id} data-testid={`qc-sample-row-${r.id}`} className="border-t border-border hover:bg-accent/40">
                    <td className="px-2 py-1.5">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-violet-900/50 text-violet-200 border border-violet-700">
                        {({ raw_material_sample: 'RM', npd_sample: 'NPD', production_sample: 'PROD' })[r.sample_type] || r.sample_type}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-foreground">{r.sample_ref}</td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.source_name || ''} onChange={v => updateRow(r, { source_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.client_name || ''} onChange={v => updateRow(r, { client_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.qc_user_name || ''} options={qcUserOpts} onChange={v => updateRow(r, { qc_user_name: v })} /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.status} options={SAMPLE_STATUSES} onChange={v => updateRow(r, { status: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.check_time || ''} onChange={v => updateRow(r, { check_time: v })} placeholder="HH:MM" /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.taste || ''} onChange={v => updateRow(r, { taste: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.aroma || ''} onChange={v => updateRow(r, { aroma: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.color || ''} onChange={v => updateRow(r, { color: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.moisture_pct} onChange={v => updateRow(r, { moisture_pct: v })} type="number" /></td>
                    <td className="px-2 py-1.5"><EditableSelect disabled={!canEdit} value={r.approval || ''} options={['', 'Yes', 'No', 'Hold']} onChange={v => updateRow(r, { approval: v })} /></td>
                    <td className="px-2 py-1.5"><EditableInput disabled={!canEdit} value={r.remarks || ''} onChange={v => updateRow(r, { remarks: v })} /></td>
                    <td className="px-2 py-1.5"><AttachmentCell kind="samples" rowId={r.id} attachments={r.attachments} onChanged={load} disabled={!canEdit} /></td>
                    <td className="px-2 py-1.5">{isAdmin && <button data-testid={`qc-sample-del-${r.id}`} onClick={() => deleteRow(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-900/30"><Trash2 size={11} /></button>}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
